import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import StripeOnboardingConfirmDialog from '@/components/StripeOnboardingConfirmDialog';
import useListingPushPrompt from '@/hooks/useListingPushPrompt';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import Navbar from '@/components/layout/Navbar';
import { SportCategoryPicker } from '@/components/sports/SportCategoryPicker';
import { 
  ArrowLeft,
  MapPin,
  Clock,
  Loader2,
  CalendarIcon,
  ImageIcon,
  CheckCircle,
  XCircle,
  Sparkles,
  Link as LinkIcon,
  Upload,
  ParkingCircle,
  Move,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Ticket,
  PoundSterling,
  ExternalLink,
  Plus,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const promptForPush = useListingPushPrompt();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [date, setDate] = useState(null);
  const [additionalDates, setAdditionalDates] = useState([]);
  const [time, setTime] = useState('19:00');
  const [endTime, setEndTime] = useState('23:00');
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'cancelled', 'failed'
  const [createdEventId, setCreatedEventId] = useState(null);
  const [flyerUploading, setFlyerUploading] = useState(false);
  const [vendorSlug, setVendorSlug] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [showStripeConfirm, setShowStripeConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const nameRef = useRef(null);
  const dateRef = useRef(null);
  const addressRef = useRef(null);
  const thumbnailRef = useRef(null);
  const flyerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: {
      address: '',
      lat: 51.5074,
      lng: -0.1278
    },
    image_url: '',
    flyer_url: '',
    flyer_is_video: false,
    ticket_link: '',
    instagram_url: '',
    has_onsite_parking: false,
    alternative_parking: '',
    requires_id: false,
    has_bar: false,
    has_food_vendors: false,
    has_toilets: false,
    has_skate_hire: false,
    nearest_stations: [''],
    thumbnail_position: { x: 50, y: 50, scale: 1 },
    tickets_enabled: false,
    promotion_only: false,
    ticket_price: '',
    ticket_quantity: '',
    max_per_buyer: 6,
    ticket_releases: [],
    releases_simultaneous: false,
    cash_payment_enabled: false,
    booking_fee_override: false,
    booking_fee_percent: '',
    booking_fee_fixed_pence: '',
    fee_passed_to_buyer: null,
    is_recurring: false,
    recurrence_pattern: '',
    // Permanent venue (admin only): when true, this becomes a venue like
    // The Snow Centre — open daily, never expires, drivers pick travel
    // dates per trip rather than the event having a fixed date.
    is_permanent_venue: false,
    // Category drives the filter pills on the Events page (All / Skate /
    // Snow Sports). Default to skate for the typical roller-skate event.
    category: 'skate',  // LEGACY — kept for backwards compat with existing API consumers.
    // Phase 3 — new canonical sport field. Defaults to roller_skate (founding community).
    // Both fields are sent to the backend during transition; backend stores `sport`.
    sport: 'roller_skate',
    // Civilian Nights — Pay & Reconcile. Mandatory yes/no on whether the
    // organiser is collecting money for this event (independent of the
    // Stripe ticketing flow above — this is the manual / bank-transfer
    // path used for raves, club nights and Civilian events).
    organiser_collects_money: null,
    cash_accepted: false,
    payment_details: {
      account_name: '',
      sort_code: '',
      account_number: '',
      reference_instruction: '',
      payment_notes: '',
    }
  });
  
  // Past events for reuse
  // Past events / "Reuse a past event" feature was removed by user request.
  // The backend duplicate endpoint (POST /api/events/{id}/duplicate) is left
  // in place for potential admin tooling.
  
  // Thumbnail upload state
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showPositioner, setShowPositioner] = useState(false);
  const thumbnailPreviewRef = useRef(null);

  // Arrow button handlers for easier touch control
  const MOVE_STEP = 10; // 10% per click
  const SCALE_STEP = 0.1; // 10% zoom per click
  
  const movePosition = (direction) => {
    setFormData(prev => {
      const { x, y, scale } = prev.thumbnail_position;
      let newX = x, newY = y;
      
      switch(direction) {
        case 'up': newY = Math.max(0, y - MOVE_STEP); break;
        case 'down': newY = Math.min(100, y + MOVE_STEP); break;
        case 'left': newX = Math.max(0, x - MOVE_STEP); break;
        case 'right': newX = Math.min(100, x + MOVE_STEP); break;
        default: break;
      }
      
      return { ...prev, thumbnail_position: { x: newX, y: newY, scale: scale || 1 } };
    });
  };
  
  const adjustScale = (direction) => {
    setFormData(prev => {
      const { x, y, scale = 1 } = prev.thumbnail_position;
      let newScale = scale;
      
      if (direction === 'in') newScale = Math.min(2, scale + SCALE_STEP);
      if (direction === 'out') newScale = Math.max(0.5, scale - SCALE_STEP);
      
      return { ...prev, thumbnail_position: { x, y, scale: newScale } };
    });
  };
  
  const resetPosition = () => {
    setFormData(prev => ({
      ...prev,
      thumbnail_position: { x: 50, y: 50, scale: 1 }
    }));
  };

  // Handle thumbnail drag to reposition
  const handleThumbnailDrag = (e) => {
    if (!isDragging || !thumbnailPreviewRef.current) return;
    
    const rect = thumbnailPreviewRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    const scale = formData.thumbnail_position.scale || 1;
    
    setFormData(prev => ({
      ...prev,
      thumbnail_position: { x, y, scale }
    }));
  };

  const handleThumbnailTouchDrag = (e) => {
    if (!isDragging || !thumbnailPreviewRef.current) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const rect = thumbnailPreviewRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((touch.clientY - rect.top) / rect.height) * 100));
    const scale = formData.thumbnail_position.scale || 1;
    
    setFormData(prev => ({
      ...prev,
      thumbnail_position: { x, y, scale }
    }));
  };

  // Check for payment callback on mount
  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    
    if (status === 'success' && sessionId) {
      verifyPaymentAndCreateEvent(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
      toast.error('Payment failed. Event not added.');
    }
  }, [searchParams]);

  // Fetch vendor space slug for "My Corner" link
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user?.is_event_organiser) {
      axios.get(`${API}/vendor-spaces/mine`, { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => { if (data?.slug) setVendorSlug(data.slug); })
        .catch(() => {});
    }
    // Check Stripe Connect status
    if (token) {
      axios.get(`${API}/drivers/stripe-connect/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => setStripeStatus(data))
        .catch(() => {});
    }
    // Past-events fetch removed — "Reuse a past event" UI was deleted.
  }, [user]);

  // handleDuplicateEvent removed — "Reuse a past event" UI was deleted.


  const handleStripeConnect = async () => {
    setStripeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const currentUrl = window.location.origin;
      const { data } = await axios.post(`${API}/drivers/stripe-connect/onboard`, {
        return_url: `${currentUrl}/create-event?stripe_return=true`,
        refresh_url: `${currentUrl}/create-event?stripe_refresh=true`
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.already_onboarded) {
        toast.success('Your payout account is already set up!');
        setStripeStatus({ status: 'complete', has_account: true });
      } else if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      }
    } catch {
      toast.error('Failed to start Stripe setup');
    } finally {
      setStripeLoading(false);
    }
  };


  const verifyPaymentAndCreateEvent = async (sessionId) => {
    setVerifying(true);
    try {
      const response = await axios.post(
        `${API}/events/verify-payment?session_id=${sessionId}`,
        {},
        {  }
      );
      
      setCreatedEventId(response.data?.event_id);
      setPaymentStatus('success');
      toast.success('Event added successfully!');
      
      // Clear URL params
      window.history.replaceState(null, '', '/create-event');
    } catch (error) {
      setPaymentStatus('failed');
      toast.error(error.response?.data?.detail || 'Payment failed. Event not added.');
    } finally {
      setVerifying(false);
    }
  };

  const handleFlyerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      toast.error('Please upload an image or video file');
      return;
    }
    
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(isVideo ? 'Video must be less than 100MB' : 'Image must be less than 10MB');
      return;
    }
    
    setFlyerUploading(true);
    try {
      // Await the FileReader via Promise so the outer try/catch/finally
      // actually wraps the full upload flow (previously the finally block
      // fired instantly after readAsDataURL, before the upload completed,
      // and errors inside onload were silently lost).
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });

      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/gcs`, {
        file_data: base64,
        filename: file.name,
        upload_type: isVideo ? 'event_video' : 'event_image'
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setFormData({ ...formData, flyer_url: response.data.url, flyer_is_video: isVideo });
        setFieldErrors(prev => ({ ...prev, flyer_url: undefined }));
        toast.success(isVideo ? 'Video flyer uploaded!' : 'Flyer uploaded successfully!');
      } else {
        toast.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload flyer');
    } finally {
      setFlyerUploading(false);
      // Reset the file input so picking the SAME file again still fires a
      // change event. Without this, deleting the preview and re-selecting
      // the same image silently does nothing on Chromium / Samsung Internet.
      if (e?.target) e.target.value = '';
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    setThumbnailUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });
      
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/gcs`, {
        file_data: base64,
        filename: file.name,
        upload_type: 'event_image'
      }, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
        setFieldErrors(prev => ({ ...prev, image_url: undefined }));
        toast.success('Thumbnail uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
      // Reset so the same file can be re-selected after deletion
      if (e?.target) e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isAdmin = !!(user?.is_admin || user?.role === 'admin');
    const isVenue = isAdmin && formData.is_permanent_venue;

    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Event name is required';
    if (!isVenue && !date) errors.date = 'Event date is required';
    if (!formData.location?.address?.trim()) errors.address = 'Venue address is required';
    if (!formData.image_url) errors.image_url = 'Event thumbnail is required';
    if (!formData.flyer_url) errors.flyer_url = 'Event flyer is required';

    // Pay & Reconcile validation — the radio is mandatory. If "Yes", at
    // least an account name + either bank fields OR payment notes are
    // required so attendees have *something* to pay against.
    if (!isVenue && formData.organiser_collects_money === null) {
      errors.organiser_collects_money = 'Please tell us if you are collecting money for this event';
    }
    if (formData.organiser_collects_money === true) {
      const pd = formData.payment_details;
      if (!pd.account_name?.trim()) {
        errors.payment_details = 'Account / payee name is required';
      } else if (!pd.payment_notes?.trim() && (!pd.sort_code?.trim() || !pd.account_number?.trim())) {
        errors.payment_details = 'Add sort code + account number, or fill the Notes field with how to pay';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstMissingLabel = Object.values(errors)[0];
      toast.error(`Missing: ${firstMissingLabel}`);
      const refMap = { name: nameRef, date: dateRef, address: addressRef, image_url: thumbnailRef, flyer_url: flyerRef };
      const firstKey = Object.keys(errors)[0];
      const target = refMap[firstKey]?.current;
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          try { target.focus({ preventScroll: true }); } catch {}
        }, 400);
      }
      return;
    }
    setFieldErrors({});

    // For permanent venues, use a 2099 sentinel date so the auto-cleanup
    // never expires them. Drivers pick their actual travel date per trip.
    let eventDate;
    let eventEndTime = null;
    if (isVenue) {
      eventDate = new Date('2099-12-31T09:00:00Z');
    } else {
      eventDate = new Date(date);
      const [hours, minutes] = time.split(':');
      eventDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      if (endTime) {
        const endDate = new Date(date);
        const [endHours, endMinutes] = endTime.split(':');
        endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
        eventEndTime = endDate.toISOString();
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      
      // Create event directly - FREE
      const response = await axios.post(`${API}/events`, {
        name: formData.name,
        description: formData.description,
        date: eventDate.toISOString(),
        end_time: eventEndTime,
        venue_type: isVenue ? 'venue' : 'event',
        category: formData.category || 'skate',
        sport: formData.sport || 'roller_skate',
        location: formData.location,
        image_url: formData.image_url,
        flyer_url: formData.flyer_url,
        ticket_link: (isVenue || formData.tickets_enabled) ? null : formData.ticket_link,
        instagram_url: formData.instagram_url,
        has_onsite_parking: formData.has_onsite_parking,
        alternative_parking: formData.alternative_parking || null,
        requires_id: formData.requires_id,
        has_bar: formData.has_bar,
        has_food_vendors: formData.has_food_vendors,
        has_toilets: formData.has_toilets,
        has_skate_hire: formData.has_skate_hire,
        nearest_stations: formData.nearest_stations.filter(s => s.trim()),
        thumbnail_position: formData.thumbnail_position,
        tickets_enabled: isVenue ? false : formData.tickets_enabled,
        ticket_price_pence: (!isVenue && formData.tickets_enabled)
          ? (formData.ticket_releases.length > 0
            ? Math.round(parseFloat(formData.ticket_releases[0]?.price || formData.ticket_price) * 100)
            : Math.round(parseFloat(formData.ticket_price) * 100))
          : null,
        ticket_quantity: (!isVenue && formData.tickets_enabled)
          ? (formData.ticket_releases.length > 0
            ? formData.ticket_releases.reduce((sum, r) => sum + (parseInt(r.quantity) || 0), 0)
            : parseInt(formData.ticket_quantity))
          : null,
        max_per_buyer: (!isVenue && formData.tickets_enabled) ? parseInt(formData.max_per_buyer) : 6,
        ticket_releases: (!isVenue && formData.tickets_enabled && formData.ticket_releases.length > 0)
          ? formData.ticket_releases.map(r => ({
              name: r.name,
              price_pence: Math.round(parseFloat(r.price) * 100),
              quantity: parseInt(r.quantity) || 0,
              end_date: r.end_date || null,
              sold: 0
            }))
          : null,
        releases_simultaneous: (!isVenue && formData.tickets_enabled) ? formData.releases_simultaneous : false,
        cash_payment_enabled: (!isVenue && formData.tickets_enabled) ? formData.cash_payment_enabled : false,
        booking_fee_percent: (!isVenue && formData.booking_fee_override) ? parseFloat(formData.booking_fee_percent) || null : null,
        booking_fee_fixed_pence: (!isVenue && formData.booking_fee_override) ? parseInt(formData.booking_fee_fixed_pence) || null : null,
        fee_passed_to_buyer: (!isVenue && formData.booking_fee_override) ? formData.fee_passed_to_buyer : null,
        is_recurring: isVenue ? false : formData.is_recurring,
        recurrence_pattern: (!isVenue && formData.is_recurring) ? formData.recurrence_pattern : null,
        additional_dates: isVenue ? [] : additionalDates.map(d => {
          const dt = new Date(d);
          const [hours, minutes] = time.split(':');
          dt.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          return dt.toISOString();
        }),
        // Civilian Nights — Pay & Reconcile fields. Sent on every create
        // so legacy events naturally get the default no-collect state.
        // Bank details are stripped on the backend if organiser_collects_money
        // is false, and stored encrypted otherwise.
        organiser_collects_money: !!formData.organiser_collects_money,
        cash_accepted: !!formData.organiser_collects_money && !!formData.cash_accepted,
        payment_details: formData.organiser_collects_money ? {
          account_name: formData.payment_details.account_name.trim(),
          sort_code: formData.payment_details.sort_code.trim(),
          account_number: formData.payment_details.account_number.trim(),
          reference_instruction: formData.payment_details.reference_instruction.trim(),
          payment_notes: formData.payment_details.payment_notes.trim(),
        } : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Event created successfully!');
      promptForPush('event');
      if (user?.is_event_organiser) {
        setCreatedEventId(response.data.event_id);
        setPaymentStatus('success');
        setLoading(false);
      } else {
        navigate(`/events/${response.data.event_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
      setLoading(false);
    }
  };

  // Show verifying state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="bg-card border-border/50 max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
            <h2 className="font-semibold text-xl">Verifying Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment and create your event.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show success state
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="bg-card border-border/50 max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <h2 className="font-heading text-2xl text-success">Event added successfully!</h2>
            <p className="text-muted-foreground">Your event has been created and is now visible to the community.</p>
            <div className="flex flex-col gap-2 pt-2">
              {createdEventId && (
                <Button 
                  onClick={() => navigate(`/events/${createdEventId}`)}
                  className="rounded-full bg-primary hover:bg-primary/90"
                  data-testid="view-created-event-btn"
                >
                  View Event
                </Button>
              )}
              <Button 
                variant={createdEventId ? "outline" : "default"}
                onClick={() => navigate('/dashboard')}
                className={`rounded-full ${!createdEventId ? 'bg-primary hover:bg-primary/90' : ''}`}
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              {user?.is_event_organiser && vendorSlug && (
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/space/${vendorSlug}`)}
                  className="rounded-full"
                >
                  View on My Corner
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show cancelled/failed state
  if (paymentStatus === 'cancelled' || paymentStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="bg-card border-border/50 max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
            <h2 className="font-heading text-2xl text-destructive">Payment failed. Event not added.</h2>
            <p className="text-muted-foreground">Your payment was not completed. No event has been created.</p>
            <div className="flex gap-3 justify-center">
              <Button 
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="rounded-full"
              >
                Go to Dashboard
              </Button>
              <Button 
                onClick={() => {
                  setPaymentStatus(null);
                  window.history.replaceState(null, '', '/create-event');
                }}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Header */}
      <div className="bg-card border-b border-border/50 px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-heading text-2xl">Upload Flyer / Create Event</h1>
        </div>
      </div>

      {/* Form */}
      <main className="px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Free Listing Notice */}
          <Alert className="mb-6 bg-success/10 border-success/30">
            <Sparkles className="w-4 h-4 text-success" />
            <AlertDescription className="text-foreground">
              <strong>List your event for FREE!</strong>
              <br />
              <span className="text-muted-foreground text-sm">
                Share your skating event with the RollM8s community at no cost.
              </span>
            </AlertDescription>
          </Alert>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* "Reuse a past event" section was removed at user request. */}

            {/* Event Details */}
            <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/30">
              <CardContent className="p-4">
                <button
                  type="button"
                  onClick={() => navigate('/create-event/auto')}
                  className="w-full flex items-center gap-3 text-left"
                  data-testid="auto-fill-flyer-cta"
                >
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Got a flyer? Auto-fill in seconds</p>
                    <p className="text-xs text-muted-foreground">Upload the image, we'll read the details</p>
                  </div>
                  <span className="text-xs text-accent font-semibold flex-shrink-0">Try it →</span>
                </button>
              </CardContent>
            </Card>

            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className={fieldErrors.name ? 'text-red-500' : ''}>Event Name *</Label>
                  <Input
                    ref={nameRef}
                    placeholder="e.g., London Roller Disco"
                    className={`h-12 rounded-xl ${fieldErrors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    maxLength={60}
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: undefined });
                    }}
                    data-testid="event-name-input"
                  />
                  {fieldErrors.name && <p className="text-xs text-red-500">{fieldErrors.name}</p>}
                  {formData.name.length > 45 && (
                    <p className="text-xs text-muted-foreground text-right">{formData.name.length}/60</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Tell people what to expect..."
                    className="min-h-24 rounded-xl"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="event-description-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Event Thumbnail Upload - REQUIRED */}
            <Card ref={thumbnailRef} className={`bg-card border-border/50 ${fieldErrors.image_url ? 'border-red-500 ring-1 ring-red-500/50' : ''}`}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center gap-2 ${fieldErrors.image_url ? 'text-red-500' : ''}`}>
                  <ImageIcon className={`w-5 h-5 ${fieldErrors.image_url ? 'text-red-500' : 'text-primary'}`} />
                  Event Thumbnail *
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className={`text-sm ${fieldErrors.image_url ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                  {fieldErrors.image_url || 'This image shows in event listings and cards'}
                </p>
                <input onClick={e => { e.target.value = null; }}
                  type="file"
                  ref={thumbnailInputRef}
                  onChange={handleThumbnailUpload}
                  accept="image/*"
                  className="hidden"
                  data-testid="thumbnail-file-input"
                />
                
                {formData.image_url ? (
                  <div className="space-y-4">
                    {/* Thumbnail Preview with Repositioning */}
                    <div 
                      ref={thumbnailPreviewRef}
                      className={`relative rounded-xl overflow-hidden border-2 ${showPositioner ? 'border-primary' : 'border-border/50'}`}
                      onMouseDown={() => { setIsDragging(true); setShowPositioner(true); }}
                      onMouseUp={() => setIsDragging(false)}
                      onMouseLeave={() => setIsDragging(false)}
                      onMouseMove={handleThumbnailDrag}
                      onTouchStart={() => { setIsDragging(true); setShowPositioner(true); }}
                      onTouchEnd={() => setIsDragging(false)}
                      onTouchMove={handleThumbnailTouchDrag}
                    >
                      <img loading="lazy" 
                        src={formData.image_url} 
                        alt="Event Thumbnail" 
                        className="w-full h-48 object-cover bg-muted/30 pointer-events-none select-none transition-transform"
                        style={{ 
                          objectPosition: `${formData.thumbnail_position.x}% ${formData.thumbnail_position.y}%`,
                          transform: `scale(${formData.thumbnail_position.scale || 1})`
                        }}
                        draggable={false}
                      />
                      {/* Position Indicator */}
                      <div 
                        className="absolute w-3 h-3 bg-primary rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ 
                          left: `${formData.thumbnail_position.x}%`, 
                          top: `${formData.thumbnail_position.y}%` 
                        }}
                      />
                    </div>
                    
                    {/* Arrow Controls for Touch-Friendly Repositioning */}
                    <div className="bg-background/50 rounded-xl p-4 border border-border/50">
                      <p className="text-xs text-muted-foreground text-center mb-3">
                        Adjust thumbnail position and zoom
                      </p>
                      
                      <div className="flex items-center justify-center gap-6">
                        {/* Directional Controls */}
                        <div className="flex flex-col items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => movePosition('up')}
                            className="w-10 h-10 p-0 rounded-lg"
                            data-testid="position-up-btn"
                          >
                            <ChevronUp className="w-5 h-5" />
                          </Button>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => movePosition('left')}
                              className="w-10 h-10 p-0 rounded-lg"
                              data-testid="position-left-btn"
                            >
                              <ChevronLeft className="w-5 h-5" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={resetPosition}
                              className="w-10 h-10 p-0 rounded-lg text-muted-foreground hover:text-foreground"
                              title="Reset to center"
                              data-testid="position-reset-btn"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => movePosition('right')}
                              className="w-10 h-10 p-0 rounded-lg"
                              data-testid="position-right-btn"
                            >
                              <ChevronRight className="w-5 h-5" />
                            </Button>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => movePosition('down')}
                            className="w-10 h-10 p-0 rounded-lg"
                            data-testid="position-down-btn"
                          >
                            <ChevronDown className="w-5 h-5" />
                          </Button>
                        </div>
                        
                        {/* Zoom Controls */}
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-xs text-muted-foreground">Zoom</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => adjustScale('in')}
                            className="w-10 h-10 p-0 rounded-lg"
                            disabled={(formData.thumbnail_position.scale || 1) >= 2}
                            data-testid="zoom-in-btn"
                          >
                            <ZoomIn className="w-5 h-5" />
                          </Button>
                          <span className="text-xs font-medium">
                            {Math.round((formData.thumbnail_position.scale || 1) * 100)}%
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => adjustScale('out')}
                            className="w-10 h-10 p-0 rounded-lg"
                            disabled={(formData.thumbnail_position.scale || 1) <= 0.5}
                            data-testid="zoom-out-btn"
                          >
                            <ZoomOut className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => thumbnailInputRef.current?.click()}
                        disabled={thumbnailUploading}
                        className="flex-1 rounded-xl"
                      >
                        {thumbnailUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Change Thumbnail
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setFormData({ ...formData, image_url: '', thumbnail_position: { x: 50, y: 50, scale: 1 } })}
                        className="rounded-xl text-destructive hover:text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => !thumbnailUploading && thumbnailInputRef.current?.click()}
                    className={`border-2 border-dashed border-primary/40 rounded-xl p-8 text-center transition-colors ${thumbnailUploading ? 'cursor-wait bg-primary/5' : 'cursor-pointer hover:border-primary/50'}`}
                  >
                    {thumbnailUploading ? (
                      <div className="space-y-3">
                        <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
                        <p className="text-primary font-medium">Uploading thumbnail...</p>
                        <p className="text-xs text-muted-foreground">Please wait</p>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">Click to upload event thumbnail</p>
                        <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB</p>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Event Flyer Upload - REQUIRED */}
            <Card ref={flyerRef} className={`bg-card border-border/50 ${fieldErrors.flyer_url ? 'border-red-500 ring-1 ring-red-500/50' : ''}`}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center gap-2 ${fieldErrors.flyer_url ? 'text-red-500' : ''}`}>
                  <Upload className={`w-5 h-5 ${fieldErrors.flyer_url ? 'text-red-500' : 'text-accent'}`} />
                  Event Flyer *
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className={`text-sm ${fieldErrors.flyer_url ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                  {fieldErrors.flyer_url || 'Full-size flyer or video shown on the event page'}
                </p>
                <input onClick={e => { e.target.value = null; }}
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFlyerUpload}
                  accept="image/*,video/*"
                  className="hidden"
                  data-testid="flyer-file-input"
                />
                
                {formData.flyer_url ? (
                  <div className="space-y-3">
                    <div className="relative rounded-xl overflow-hidden border border-border/50">
                      {formData.flyer_is_video || formData.flyer_url.match(/\.(mp4|mov|webm|m4v)$/i) ? (
                        <video 
                          src={formData.flyer_url} 
                          className="w-full max-h-64 object-contain bg-muted/30"
                          muted autoPlay loop playsInline controls
                        />
                      ) : (
                        <img loading="lazy" 
                          src={formData.flyer_url} 
                          alt="Event Flyer" 
                          className="w-full max-h-64 object-contain bg-muted/30"
                        />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={flyerUploading}
                        className="flex-1 rounded-xl"
                      >
                        {flyerUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Change Flyer
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setFormData({ ...formData, flyer_url: '' })}
                        className="rounded-xl text-destructive hover:text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => !flyerUploading && fileInputRef.current?.click()}
                    className={`border-2 border-dashed border-primary/40 rounded-xl p-8 text-center transition-colors ${flyerUploading ? 'cursor-wait bg-primary/5' : 'cursor-pointer hover:border-primary/50'}`}
                  >
                    {flyerUploading ? (
                      <div className="space-y-3">
                        <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
                        <p className="text-primary font-medium">Uploading your flyer...</p>
                        <p className="text-xs text-muted-foreground">Please wait</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">Click to upload your event flyer</p>
                        <p className="text-xs text-muted-foreground mt-1">Images or videos — PNG, JPG, MP4 up to 100MB</p>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tickets */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-primary" />
                  Tickets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Toggle: Sell through rollm8s, external link, or promotion only */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={formData.tickets_enabled ? "default" : "outline"}
                    className="rounded-full text-xs sm:text-sm"
                    onClick={() => setFormData({ ...formData, tickets_enabled: true, ticket_link: '', promotion_only: false, ticket_releases: formData.ticket_releases.length > 0 ? formData.ticket_releases : [
                      { name: '', price: '', quantity: '', end_date: '' },
                      { name: '', price: '', quantity: '', end_date: '' }
                    ] })}
                    data-testid="tickets-sell-btn"
                  >
                    Sell Here
                  </Button>
                  <Button
                    type="button"
                    variant={!formData.tickets_enabled && !formData.promotion_only ? "default" : "outline"}
                    className="rounded-full text-xs sm:text-sm"
                    onClick={() => setFormData({ ...formData, tickets_enabled: false, promotion_only: false, ticket_price: '', ticket_quantity: '' })}
                    data-testid="tickets-link-btn"
                  >
                    External Link
                  </Button>
                  <Button
                    type="button"
                    variant={formData.promotion_only ? "default" : "outline"}
                    className="rounded-full text-xs sm:text-sm"
                    onClick={() => setFormData({ ...formData, tickets_enabled: false, promotion_only: true, ticket_link: '', ticket_price: '', ticket_quantity: '', ticket_releases: [] })}
                    data-testid="tickets-promo-btn"
                  >
                    Promotion
                  </Button>
                </div>

                {formData.promotion_only ? (
                  <div className="p-4 rounded-xl bg-muted/20 border border-border/30 text-center space-y-1">
                    <p className="text-sm font-medium">Promotion only</p>
                    <p className="text-xs text-muted-foreground">No tickets — just list your event for visibility and ride-sharing</p>
                  </div>
                ) : formData.tickets_enabled ? (
                  <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                      Sell tickets directly through rollm8s. We charge £0.30 + 2% per ticket — cheaper than Eventbrite.
                    </p>
                    
                    {/* Ticket Releases — always visible */}
                    {formData.ticket_releases.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-semibold">Ticket Releases</Label>
                          <label className="flex items-center gap-3 cursor-pointer px-3 py-2.5 rounded-xl border border-primary/30 bg-primary/5" data-testid="simultaneous-toggle">
                            <input type="checkbox" checked={formData.releases_simultaneous}
                              onChange={(e) => setFormData({ ...formData, releases_simultaneous: e.target.checked })}
                              className="w-5 h-5 accent-primary rounded" />
                            <span className="text-sm font-medium">All available at once</span>
                          </label>
                        </div>
                        {formData.releases_simultaneous && (
                          <p className="text-[11px] text-primary/70 -mt-1">Buyers can choose between types (e.g. VIP, GA, Under-16s)</p>
                        )}
                        {formData.ticket_releases.map((release, idx) => (
                          <div key={idx} className="p-3 rounded-xl border border-border/50 bg-background/30 space-y-2" data-testid={`release-card-${idx}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-primary">Ticket Type {idx + 1}</span>
                              {formData.ticket_releases.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = formData.ticket_releases.filter((_, i) => i !== idx);
                                    setFormData({ ...formData, ticket_releases: updated });
                                  }}
                                  className="text-xs text-destructive hover:underline"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <Input
                              placeholder="Name this release"
                              className="h-9 rounded-lg text-sm"
                              value={release.name}
                              onChange={(e) => {
                                const updated = [...formData.ticket_releases];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setFormData({ ...formData, ticket_releases: updated });
                              }}
                              data-testid={`release-name-${idx}`}
                            />
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <Label className="text-xs">Price (£)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00 = Free"
                                  className="h-9 text-sm rounded-lg"
                                  value={release.price}
                                  onChange={(e) => {
                                    const updated = [...formData.ticket_releases];
                                    updated[idx] = { ...updated[idx], price: e.target.value };
                                    setFormData({ ...formData, ticket_releases: updated });
                                  }}
                                  data-testid={`release-price-${idx}`}
                                />
                                {(release.price === '0' || release.price === '0.00' || release.price === '') && release.price !== '' && parseFloat(release.price) === 0 && (
                                  <span className="text-[10px] text-green-400 font-semibold mt-0.5 block">FREE</span>
                                )}
                              </div>
                              <div>
                                <Label className="text-xs">Qty</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  placeholder="50"
                                  className="h-9 text-sm rounded-lg"
                                  value={release.quantity}
                                  onChange={(e) => {
                                    const updated = [...formData.ticket_releases];
                                    updated[idx] = { ...updated[idx], quantity: e.target.value };
                                    setFormData({ ...formData, ticket_releases: updated });
                                  }}
                                  data-testid={`release-qty-${idx}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Ends</Label>
                                <Input
                                  type="date"
                                  className="h-9 text-sm rounded-lg"
                                  value={release.end_date}
                                  onChange={(e) => {
                                    const updated = [...formData.ticket_releases];
                                    updated[idx] = { ...updated[idx], end_date: e.target.value };
                                    setFormData({ ...formData, ticket_releases: updated });
                                  }}
                                  data-testid={`release-end-${idx}`}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, ticket_releases: [...formData.ticket_releases, { name: '', price: '', quantity: '', end_date: '' }] })}
                          className="w-full py-2 text-xs text-primary border border-dashed border-primary/30 rounded-xl hover:bg-primary/5"
                          data-testid="add-another-release-btn"
                        >
                          + Add another release
                        </button>
                        <div className="text-xs text-muted-foreground bg-muted/20 p-2 rounded-lg">
                          Total tickets: {formData.ticket_releases.reduce((sum, r) => sum + (parseInt(r.quantity) || 0), 0)} across {formData.ticket_releases.length} release{formData.ticket_releases.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}

                    {/* Cash at Door toggle */}
                    <label className="flex items-center gap-3 cursor-pointer px-3 py-2.5 rounded-xl border border-border/30 bg-muted/10 hover:border-primary/30 transition-colors" data-testid="cash-payment-toggle">
                      <input type="checkbox" checked={formData.cash_payment_enabled}
                        onChange={(e) => setFormData({ ...formData, cash_payment_enabled: e.target.checked })}
                        className="w-5 h-5 accent-primary rounded" />
                      <div>
                        <span className="text-sm font-medium">Accept cash at door</span>
                        <p className="text-[11px] text-muted-foreground">Buyers can reserve tickets and pay cash at your event</p>
                      </div>
                    </label>

                    <div>
                      <Label>Max per buyer</Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        className="h-12 rounded-xl"
                        value={formData.max_per_buyer}
                        onChange={(e) => setFormData({ ...formData, max_per_buyer: parseInt(e.target.value) || 6 })}
                        data-testid="ticket-max-input"
                      />
                    </div>
                    {/* Fee breakdown - use first release price or base price */}
                    {(() => {
                      const displayPrice = formData.ticket_releases.length > 0
                        ? parseFloat(formData.ticket_releases[0]?.price) || 0
                        : parseFloat(formData.ticket_price) || 0;
                      if (!displayPrice) return null;
                      const customPercent = formData.booking_fee_override ? parseFloat(formData.booking_fee_percent) || 2 : 2;
                      const customFixed = formData.booking_fee_override ? parseInt(formData.booking_fee_fixed_pence) || 30 : 30;
                      const platformMinPercent = 2;
                      const platformMinFixed = 30;
                      const totalFee = (customFixed / 100) + (displayPrice * customPercent / 100);
                      const platformMinFee = (platformMinFixed / 100) + (displayPrice * platformMinPercent / 100);
                      const organiserExtra = Math.max(0, totalFee - platformMinFee);
                      const buyerPaysFee = formData.booking_fee_override ? formData.fee_passed_to_buyer !== false : true;
                      const youReceive = buyerPaysFee ? displayPrice + organiserExtra : displayPrice - platformMinFee;
                      const releaseLabel = formData.ticket_releases.length > 0 ? ` (${formData.ticket_releases[0]?.name || 'Ticket Type 1'})` : '';
                      return (
                      <div className="bg-muted/30 p-3 rounded-xl text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Ticket price{releaseLabel}</span>
                          <span>£{displayPrice.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Booking fee</span>
                          <span>£{totalFee.toFixed(2)}</span>
                        </div>
                        {organiserExtra > 0.001 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Platform min fee</span>
                            <span>£{platformMinFee.toFixed(2)}</span>
                          </div>
                        )}
                        {organiserExtra > 0.001 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Extra fee (yours)</span>
                            <span className="text-success">+£{organiserExtra.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-medium pt-1 border-t border-border/50">
                          <span>{buyerPaysFee ? 'You receive' : 'You receive (fee absorbed)'}</span>
                          <span className="text-success">£{youReceive.toFixed(2)}</span>
                        </div>
                      </div>
                      );
                    })()}

                    {/* Fee Override Section */}
                    <div className="border-t border-border/30 pt-3 mt-3">
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={formData.booking_fee_override}
                          onChange={(e) => setFormData({ ...formData, booking_fee_override: e.target.checked, fee_passed_to_buyer: e.target.checked ? true : null })}
                          className="rounded"
                          data-testid="fee-override-toggle"
                        />
                        <Label className="text-sm">Customise booking fee for this event</Label>
                      </div>
                      {formData.booking_fee_override && (
                        <div className="space-y-2 pl-1 animate-fade-in">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Fee %</Label>
                              <Input
                                type="number"
                                step="0.5"
                                min="0"
                                placeholder="2"
                                value={formData.booking_fee_percent}
                                onChange={(e) => setFormData({ ...formData, booking_fee_percent: e.target.value })}
                                className="h-9 text-sm"
                                data-testid="event-fee-percent"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Fixed fee (pence)</Label>
                              <Input
                                type="number"
                                min="0"
                                placeholder="30"
                                value={formData.booking_fee_fixed_pence}
                                onChange={(e) => setFormData({ ...formData, booking_fee_fixed_pence: e.target.value })}
                                className="h-9 text-sm"
                                data-testid="event-fee-fixed"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.fee_passed_to_buyer !== false}
                              onChange={(e) => setFormData({ ...formData, fee_passed_to_buyer: e.target.checked })}
                              className="rounded"
                              data-testid="event-fee-buyer-pays"
                            />
                            <Label className="text-xs">Buyer pays booking fee on top</Label>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Stripe Connect Prompt */}
                    {stripeStatus && stripeStatus.status !== 'complete' && (
                      <div className="border-t border-border/30 pt-3 mt-3">
                        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 space-y-3">
                          <p className="text-sm font-semibold text-primary">Get paid directly to your bank</p>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            We use Stripe — the same payment system behind Uber, Deliveroo and Amazon.
                            Setup takes about <span className="text-foreground font-medium">2 minutes</span>. You'll just need:
                          </p>
                          <div className="grid grid-cols-1 gap-1.5 text-xs text-muted-foreground pl-1">
                            <span className="flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                              Your name &amp; date of birth
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                              Bank details (sort code &amp; account number)
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                              A photo of your ID (passport or driving licence)
                            </span>
                          </div>
                          <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
                            Once connected, ticket revenue is automatically paid out to your bank 48 hours after each event. No chasing payments, no invoices.
                          </p>
                          <Button
                            size="sm"
                            onClick={() => setShowStripeConfirm(true)}
                            disabled={stripeLoading}
                            className="rounded-full w-full gap-2"
                            data-testid="event-stripe-connect-btn"
                          >
                            {stripeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                            {stripeStatus?.status === 'pending' ? 'Continue Stripe Setup' : 'Set Up in 2 Minutes'}
                          </Button>
                        </div>
                      </div>
                    )}
                    {stripeStatus?.status === 'complete' && (
                      <div className="border-t border-border/30 pt-3 mt-3">
                        <div className="flex items-center gap-2 text-xs text-green-400">
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span>Stripe connected — ticket revenue goes directly to your account</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>External ticket link (optional)</Label>
                    <div className="relative">
                      <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        placeholder="https://eventbrite.co.uk/..."
                        className="pl-10 h-12 rounded-xl"
                        value={formData.ticket_link}
                        onChange={(e) => setFormData({ ...formData, ticket_link: e.target.value })}
                        data-testid="ticket-link-input"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Civilian Nights — Pay & Reconcile */}
            <Card className="bg-card border-border/50" data-testid="pay-reconcile-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-primary" />
                  Pay & Reconcile
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Skip if tickets above cover it. For raves / club nights / Civilian events where attendees pay you directly (bank transfer, PayPal, cash on the night).
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Required radio — collecting money? */}
                <div className="space-y-2" data-testid="organiser-collects-money-radio-row">
                  <Label className={`text-sm font-semibold ${fieldErrors.organiser_collects_money ? 'text-red-500' : ''}`}>
                    Are you collecting money for this event? *
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={formData.organiser_collects_money === true ? 'default' : 'outline'}
                      className="h-11 rounded-xl"
                      onClick={() => setFormData({ ...formData, organiser_collects_money: true })}
                      data-testid="organiser-collects-yes"
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={formData.organiser_collects_money === false ? 'default' : 'outline'}
                      className="h-11 rounded-xl"
                      onClick={() => setFormData({
                        ...formData,
                        organiser_collects_money: false,
                        cash_accepted: false,
                        payment_details: { account_name: '', sort_code: '', account_number: '', reference_instruction: '', payment_notes: '' },
                      })}
                      data-testid="organiser-collects-no"
                    >
                      No / Free entry
                    </Button>
                  </div>
                  {fieldErrors.organiser_collects_money && (
                    <p className="text-xs text-red-500">{fieldErrors.organiser_collects_money}</p>
                  )}
                </div>

                {formData.organiser_collects_money === true && (
                  <div className="space-y-4 pt-2 border-t border-border/30">
                    {/* Cash radio */}
                    <div className="space-y-2" data-testid="cash-accepted-radio-row">
                      <Label className="text-sm font-semibold">Accept cash on the night?</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant={formData.cash_accepted === true ? 'default' : 'outline'}
                          className="h-11 rounded-xl"
                          onClick={() => setFormData({ ...formData, cash_accepted: true })}
                          data-testid="cash-accepted-yes"
                        >
                          Yes
                        </Button>
                        <Button
                          type="button"
                          variant={formData.cash_accepted === false ? 'default' : 'outline'}
                          className="h-11 rounded-xl"
                          onClick={() => setFormData({ ...formData, cash_accepted: false })}
                          data-testid="cash-accepted-no"
                        >
                          Bank only
                        </Button>
                      </div>
                    </div>

                    {/* Bank details — only visible to RSVP'd Going attendees
                        on the event page, so safe to capture here. Stored
                        encrypted server-side. */}
                    <div className="space-y-3 p-3 rounded-xl border border-border/30 bg-muted/10">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-muted-foreground leading-snug">
                          Bank details are encrypted at rest and only shown to attendees who tap <span className="font-semibold text-foreground">I&apos;m Going</span> — bots & passers-by never see them.
                        </p>
                      </div>

                      <div>
                        <Label className={`text-xs ${fieldErrors.payment_details ? 'text-red-500' : ''}`}>Account / payee name *</Label>
                        <Input
                          placeholder="e.g. Jane Smith"
                          className="h-11 rounded-xl"
                          value={formData.payment_details.account_name}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_details: { ...formData.payment_details, account_name: e.target.value }
                          })}
                          data-testid="pay-account-name-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Sort code</Label>
                          <Input
                            placeholder="00-00-00"
                            className="h-11 rounded-xl"
                            value={formData.payment_details.sort_code}
                            onChange={(e) => setFormData({
                              ...formData,
                              payment_details: { ...formData.payment_details, sort_code: e.target.value }
                            })}
                            data-testid="pay-sort-code-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Account number</Label>
                          <Input
                            placeholder="12345678"
                            className="h-11 rounded-xl"
                            value={formData.payment_details.account_number}
                            onChange={(e) => setFormData({
                              ...formData,
                              payment_details: { ...formData.payment_details, account_number: e.target.value }
                            })}
                            data-testid="pay-account-number-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">Reference instruction (optional)</Label>
                        <Input
                          placeholder="Use your name as the reference"
                          className="h-11 rounded-xl"
                          value={formData.payment_details.reference_instruction}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_details: { ...formData.payment_details, reference_instruction: e.target.value }
                          })}
                          data-testid="pay-reference-input"
                        />
                      </div>

                      <div>
                        <Label className="text-xs">Notes (optional)</Label>
                        <Textarea
                          placeholder="PayPal: @janesmith · Revolut tag · or anything else attendees should know."
                          className="rounded-xl min-h-[64px]"
                          value={formData.payment_details.payment_notes}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_details: { ...formData.payment_details, payment_notes: e.target.value }
                          })}
                          data-testid="pay-notes-input"
                        />
                      </div>

                      {fieldErrors.payment_details && (
                        <p className="text-xs text-red-500">{fieldErrors.payment_details}</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Date & Time */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-primary" />
                  Date & Time
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Phase 3 — Sport category picker. Drives the multi-sport
                    filter pills on the Events page. The user picks one of
                    the seven communities. Legacy `category` field is kept
                    in sync for any pre-Phase-3 consumers still reading it. */}
                <div className="space-y-2" data-testid="category-picker">
                  <Label className="text-sm font-semibold">Sport / Community *</Label>
                  <p className="text-xs text-muted-foreground">
                    Which crew is this event for? Drives who sees it on the Events page by default.
                  </p>
                  <SportCategoryPicker
                    value={formData.sport}
                    onChange={(sportId) => setFormData(prev => ({
                      ...prev,
                      sport: sportId,
                      // Keep legacy category in sync — roller_skate maps back to "skate" for old consumers.
                      category: sportId === 'snow_sports' ? 'snow_sports' : (sportId === 'roller_skate' ? 'skate' : sportId),
                    }))}
                  />
                </div>

                {/* Admin-only: toggle this event to a Permanent Venue (Snow
                    Centre style — open daily, never expires, drivers pick
                    travel dates per trip). */}
                {(user?.is_admin || user?.role === 'admin') && (
                  <div className="flex items-center justify-between gap-3 p-3 rounded-xl border-2 border-cyan-400/30 bg-cyan-400/5" data-testid="permanent-venue-toggle-row">
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="permanent-venue-toggle" className="text-sm font-semibold text-cyan-300 cursor-pointer">
                        Permanent Venue (admin only)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Open daily — riders pick their own travel date when booking. Hides date/time/tickets.
                      </p>
                    </div>
                    <Switch
                      id="permanent-venue-toggle"
                      checked={!!formData.is_permanent_venue}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_permanent_venue: v }))}
                      data-testid="permanent-venue-toggle"
                    />
                  </div>
                )}

                {formData.is_permanent_venue && (user?.is_admin || user?.role === 'admin') ? (
                  <div className="p-4 rounded-xl border border-cyan-400/30 bg-cyan-400/5 text-sm" data-testid="permanent-venue-info">
                    <p className="font-semibold text-cyan-300">No fixed date — venue is open daily.</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Drivers offering rides to this venue will pick their own travel date and time. The venue will never auto-expire from the events list.
                    </p>
                  </div>
                ) : (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2" ref={dateRef}>
                    <Label className={fieldErrors.date ? 'text-red-500' : ''}>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full h-12 justify-start text-left font-normal rounded-xl overflow-hidden text-ellipsis whitespace-nowrap",
                            !date && "text-muted-foreground",
                            fieldErrors.date && "border-red-500 ring-1 ring-red-500"
                          )}
                          data-testid="date-picker-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{date ? format(date, "dd MMM yyyy") : "Pick a date"}</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-card border-border/50" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => {
                            setDate(d);
                            if (fieldErrors.date) setFieldErrors({ ...fieldErrors, date: undefined });
                          }}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {fieldErrors.date && <p className="text-xs text-red-500">{fieldErrors.date}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label>Start Time *</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="time"
                        className="pl-10 h-12 rounded-xl w-full"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        data-testid="time-input"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>End Time (optional)</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="time"
                      className="pl-10 h-12 rounded-xl"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      data-testid="end-time-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    When does your event finish?
                  </p>
                </div>

                {/* Additional Dates for multi-day events */}
                <div className="border-t border-border/30 pt-3 mt-1">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-primary" />
                    Additional Dates
                    <span className="text-xs text-muted-foreground font-normal">(multi-date events)</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">Event stays listed until the last date passes</p>
                  {additionalDates.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <Input type="date" value={d} className="flex-1 h-9 rounded-lg text-sm"
                        onChange={e => { const updated = [...additionalDates]; updated[i] = e.target.value; setAdditionalDates(updated); }}
                        data-testid={`additional-date-${i}`}
                      />
                      <button onClick={() => setAdditionalDates(additionalDates.filter((_, j) => j !== i))}
                        className="p-1.5 rounded-lg hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="rounded-lg text-xs"
                    onClick={() => setAdditionalDates([...additionalDates, ''])}
                    data-testid="add-date-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" />Add Date
                  </Button>
                </div>

                {/* Recurring Event */}
                <div className="border-t border-border/30 pt-3 mt-1">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                      className="rounded"
                      data-testid="recurring-event-toggle"
                    />
                    <div>
                      <Label className="text-sm">Recurring event</Label>
                      <p className="text-xs text-muted-foreground">This event happens regularly</p>
                    </div>
                  </div>
                  {formData.is_recurring && (
                    <div className="mt-2 ml-6">
                      <select
                        className="h-10 w-full rounded-xl bg-card border border-border px-3 text-sm"
                        value={formData.recurrence_pattern}
                        onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value })}
                        data-testid="recurrence-pattern-select"
                      >
                        <option value="">Select frequency</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Every 2 weeks</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  )}
                </div>
                </>
                )}
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-secondary" />
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2" ref={addressRef}>
                  <Label className={fieldErrors.address ? 'text-red-500' : ''}>Venue Address *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      placeholder="e.g., Alexandra Palace, London N22 7AY"
                      className={`pl-10 h-12 rounded-xl ${fieldErrors.address ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      value={formData.location.address}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          location: { ...formData.location, address: e.target.value }
                        });
                        if (fieldErrors.address) setFieldErrors({ ...fieldErrors, address: undefined });
                      }}
                      data-testid="location-input"
                    />
                  </div>
                  {fieldErrors.address ? (
                    <p className="text-xs text-red-500">{fieldErrors.address}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Enter the full address including postcode
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Venue Information */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                  Venue Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
                    <Label className="text-sm">ID Required</Label>
                  </div>
                  <Switch
                    checked={formData.requires_id}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_id: checked })}
                    data-testid="requires-id-switch"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <Label className="text-sm">Fully Licensed Bar</Label>
                  </div>
                  <Switch
                    checked={formData.has_bar}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_bar: checked })}
                    data-testid="has-bar-switch"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" /></svg>
                    <Label className="text-sm">Food Sold on Premises</Label>
                  </div>
                  <Switch
                    checked={formData.has_food_vendors}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_food_vendors: checked })}
                    data-testid="has-food-switch"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></svg>
                    <Label className="text-sm">Toilets Available</Label>
                  </div>
                  <Switch
                    checked={formData.has_toilets}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_toilets: checked })}
                    data-testid="has-toilets-switch"
                  />
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    <Label className="text-sm">Skate Hire Available</Label>
                  </div>
                  <Switch
                    checked={formData.has_skate_hire}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_skate_hire: checked })}
                    data-testid="has-skate-hire-switch"
                  />
                </div>

                {/* Nearest Stations */}
                <div className="pt-3 border-t border-border/30">
                  <Label className="text-sm font-medium mb-2 block">Nearest Train/Tube Station</Label>
                  {formData.nearest_stations.map((station, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <div className="relative flex-1">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 11h8m-4 4v4m-4 0h8M5 3h14a1 1 0 011 1v12a3 3 0 01-3 3H7a3 3 0 01-3-3V4a1 1 0 011-1z" /></svg>
                        <Input
                          placeholder="e.g. London Bridge"
                          className="pl-10 h-10 rounded-xl"
                          value={station}
                          onChange={(e) => {
                            const updated = [...formData.nearest_stations];
                            updated[idx] = e.target.value;
                            setFormData({ ...formData, nearest_stations: updated });
                          }}
                          data-testid={`station-input-${idx}`}
                        />
                      </div>
                      {formData.nearest_stations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, nearest_stations: formData.nearest_stations.filter((_, i) => i !== idx) })}
                          className="text-destructive hover:text-destructive/80 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, nearest_stations: [...formData.nearest_stations, ''] })}
                    className="text-xs text-primary hover:underline"
                    data-testid="add-station-btn"
                  >
                    + Add another station
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Parking Information */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ParkingCircle className="w-5 h-5 text-accent" />
                  Parking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-background/50 border border-border/50">
                  <div className="flex items-center gap-3">
                    <ParkingCircle className="w-6 h-6 text-primary" />
                    <div>
                      <Label className="text-base">Onsite Parking Available</Label>
                      <p className="text-sm text-muted-foreground">Does the venue have its own car park?</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.has_onsite_parking}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_onsite_parking: checked })}
                    data-testid="onsite-parking-switch"
                  />
                </div>
                
                {!formData.has_onsite_parking && (
                  <div className="space-y-2">
                    <Label>Alternative Parking Options (optional)</Label>
                    <Textarea
                      placeholder="e.g., Street parking on nearby roads, NCP car park 5 min walk, Pay & display on High Street..."
                      className="min-h-20 rounded-xl"
                      value={formData.alternative_parking}
                      onChange={(e) => setFormData({ ...formData, alternative_parking: e.target.value })}
                      data-testid="alternative-parking-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Help drivers know where they can park nearby
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full h-14 rounded-xl bg-primary hover:bg-primary/90 font-bold text-lg neon-glow"
              disabled={loading}
              data-testid="create-event-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Event...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create Event
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Your event will be visible to the RollM8s community immediately.
            </p>
          </form>
        </div>
      </main>
      <StripeOnboardingConfirmDialog
        open={showStripeConfirm}
        onOpenChange={setShowStripeConfirm}
        onConfirm={handleStripeConnect}
        loading={stripeLoading}
        audience="organiser"
      />
    </div>
  );
};

export default CreateEventPage;
